if (!customElements.get('facet-filters-form')) {
  class FacetFiltersForm extends HTMLElement {
    constructor() {
      super();
      this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    }

    connectedCallback() {
      const connection = this.connection = (this.connection || 0) + 1;
      themeBase.onDOMContentLoadedTrigger(() => {
        if (!this.isConnected || connection !== this.connection || this.listeners) return;
        const form = this.querySelector('form');
        if (!form) return;
        this.listeners = new AbortController();
        const options = { signal: this.listeners.signal };
        form.addEventListener('input', event => {
          if (event.target.closest('.js-facets-no-input') || event.target.classList.contains('js-facets-ignore-checkbox')) return;
          clearTimeout(this.submitTimer);
          FacetFiltersForm.cancelPending();
          if (event.target.type === 'checkbox' || event.target.tagName === 'FORM' || event.target.tagName === 'SELECT') {
            this.onSubmitHandler(event);
          } else {
            this.submitTimer = setTimeout(() => {
              if (this.isConnected && event.target.isConnected) this.onSubmitHandler(event);
            }, 500);
          }
        }, options);
        form.addEventListener('keyup', onKeyUpEscape, options);
        form.addEventListener('click', event => {
          const summary = event.target.closest('summary');
          if (summary?.closest('accordion-button')) {
            summary.setAttribute('aria-expanded', String(summary.classList.contains('animated-dropdown__button--open')));
          }
        }, options);
        // Native details toggles also cover keyboard and programmatic closure.
        form.addEventListener('toggle', event => {
          if (event.target.tagName === 'DETAILS') FacetFiltersForm.setSummarySemantics(event.target);
        }, { ...options, capture: true });
      });
    }

    disconnectedCallback() {
      this.connection += 1;
      clearTimeout(this.submitTimer);
      this.listeners?.abort();
      this.listeners = null;
    }

    static setListeners() {
      window.addEventListener('popstate', () => {
        document.querySelectorAll('facet-filters-form, facet-sorting-clone-form').forEach(form => clearTimeout(form.submitTimer));
        const params = window.location.search.slice(1);
        FacetFiltersForm.renderPage(params, null, false);
      });
    }

    static toggleActiveFacets(disable = true) {
      document.querySelectorAll('.js-facet-remove').forEach((element) => {
        element.classList.toggle('disabled', disable);
      });
    }

    static cancelPending() {
      FacetFiltersForm.requestId += 1;
      FacetFiltersForm.controller?.abort();
      FacetFiltersForm.pendingKey = null;
      FacetFiltersForm.setLoading(false);
    }

    static setLoading(loading) {
      ['ProductCount', 'ProductCountMobile', 'ProductCountDesktop'].forEach(id => {
        document.getElementById(id)?.classList.toggle('loading', loading);
      });
      const grid = document.getElementById('ProductGridContainer');
      grid?.querySelector('.collection')?.classList.toggle('loading', loading);
      if (grid) grid.setAttribute('aria-busy', String(loading));
      document.querySelectorAll('price-slider').forEach(slider => {
        if (!loading) slider.classList.remove('price-slider--disable');
      });
      if (!loading) FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderPage(searchParams, event, updateURLHash = true, options = {}) {
      const key = `${searchParams}|${options.renderProductMethod || 'replace'}`;
      if (FacetFiltersForm.pendingKey === key) return FacetFiltersForm.pendingPromise;
      FacetFiltersForm.cancelPending();
      const requestId = FacetFiltersForm.requestId;
      const controller = FacetFiltersForm.controller = new AbortController();
      const grid = document.getElementById('ProductGridContainer');
      if (!grid) return Promise.resolve(false);
      const current = () => requestId === FacetFiltersForm.requestId && grid.isConnected &&
        grid === document.getElementById('ProductGridContainer');
      const context = { current, signal: controller.signal };
      FacetFiltersForm.pendingKey = key;
      FacetFiltersForm.setLoading(true);
      document.getElementById('PmcFacetError')?.remove();
      const timeout = setTimeout(() => controller.abort(), 15000);
      const run = async () => {
        try {
          const sections = FacetFiltersForm.getSections();
          for (const section of sections) {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const cached = FacetFiltersForm.filterData.find(item => item.url === url);
            const html = cached ? cached.html : await FacetFiltersForm.renderSectionFromFetch(url, context);
            if (!current()) return false;
            const parsed = new DOMParser().parseFromString(
              lazyTemplateController.cleanHTMLWithTemplate(window.appearanceEffect ? appearanceEffect.cleanHTMLWithEffect(html) : html), 'text/html');
            if (!parsed.querySelector('#ProductGridContainer')) throw new Error('Missing product section');
            if (!current()) return false;
            FacetFiltersForm.renderFilters(parsed, event);
            FacetFiltersForm.renderProductGridContainer(parsed, options);
            FacetFiltersForm.renderProductCount(parsed);
            FacetFiltersForm.renderLoadMore(parsed, options);
          }
          if (!current()) return false;
          FacetFiltersForm.searchParamsPrev = searchParams;
          if (updateURLHash && window.location.search.slice(1) !== searchParams) FacetFiltersForm.updateURLHash(searchParams);
          options.callback?.();
          return true;
        } catch (error) {
          if (!current()) return false;
          const notice = document.createElement('p');
          notice.id = 'PmcFacetError';
          notice.setAttribute('role', 'alert');
          notice.textContent = 'Results could not be updated. ';
          const retry = document.createElement('a');
          retry.href = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
          retry.textContent = 'Retry these filters';
          notice.appendChild(retry);
          grid.prepend(notice);
          return false;
        } finally {
          clearTimeout(timeout);
          if (requestId === FacetFiltersForm.requestId) {
            FacetFiltersForm.pendingKey = null;
            FacetFiltersForm.setLoading(false);
          }
        }
      };
      FacetFiltersForm.pendingPromise = run();
      return FacetFiltersForm.pendingPromise;
    }

    static async renderSectionFromFetch(url, context) {
      const response = await fetch(url, { signal: context.signal });
      if (!response.ok) throw new Error('Filter request failed');
      const html = await response.text();
      if (context.current()) {
        FacetFiltersForm.filterData = FacetFiltersForm.filterData.filter(item => item.url !== url);
        FacetFiltersForm.filterData.push({ url, html });
        if (FacetFiltersForm.filterData.length > 30) FacetFiltersForm.filterData.shift();
      }
      return html;
    }

    static renderLoadMore(parsedHTML, options = {}) {
      if (options.renderProductMethod === 'append') {
        const target = document.getElementById('FacetsLoadMoreButton');
        const source = parsedHTML.querySelector('#FacetsLoadMoreButton');
        if (target) target.innerHTML = source?.innerHTML || '';
      }
    }

    static renderProductGridContainer(parsedHTML, options = {}) {
      if (options.renderProductMethod === 'append') {
        const lastScrollY = window.scrollY;
        const productGrid = document.getElementById('product-grid');

        const source = parsedHTML.querySelector('#product-grid');
        if (!source) throw new Error('Missing appended products');
        productGrid.append(...Array.from(source.childNodes));
        document.getElementById('ProductGridContainer').querySelector('.collection').classList.remove('loading');
        window.scrollTo(0, lastScrollY);
      } else {
        const productGridContainer = document.getElementById('ProductGridContainer');

        productGridContainer.innerHTML = parsedHTML.querySelector('#ProductGridContainer').innerHTML;
      }

      document.querySelectorAll('facets-grid').forEach(element => element.update());

      document.querySelectorAll('price-slider').forEach(slider => {
        if (slider.wrapper) slider.onResize();
      });
      window.dispatchEvent(new CustomEvent('updateStickySidebar'));
      window.lazyImages.find();
    }

    static renderProductCount(parsedHTML) {
      const source = parsedHTML.querySelector('#ProductCount');
      if (!source) return;

      ['ProductCount', 'ProductCountMobile', 'ProductCountDesktop'].forEach((id) => {
        const container = document.getElementById(id);
        if (!container) return;
        container.innerHTML = source.innerHTML;
        container.classList.remove('loading');
      });
    }

    static filterGroupKey(detail) {
      return detail?.querySelector('input[name]')?.name ||
        detail?.querySelector('template')?.content.querySelector('input[name]')?.name || null;
    }

    static setSummarySemantics(detail) {
      const summary = detail.querySelector(':scope > summary');
      if (!summary) return;
      summary.setAttribute('role', 'button');
      const accordion = detail.closest('accordion-button');
      summary.setAttribute('aria-expanded', String(accordion ? accordion.classList.contains('animated-dropdown--active') : detail.open));
      const panel = summary.nextElementSibling;
      if (panel?.id) summary.setAttribute('aria-controls', panel.id);
      else summary.removeAttribute('aria-controls');
    }

    static focusIdentity(element) {
      if (!element) return null;
      return {
        tag: element.tagName, name: element.name || '', type: element.type || '',
        value: element.value, text: element.textContent?.trim(),
        // Classes identify Apply, Back, Clear and Close independently of IDs/order.
        classes: Array.from(element.classList).filter(name => name !== 'focused').sort().join(' ')
      };
    }

    static findFocusReplacement(scope, identity) {
      if (!scope || !identity) return null;
      return Array.from(scope.querySelectorAll('summary, input, select, textarea, button, a[href]')).find(element => {
        const candidate = FacetFiltersForm.focusIdentity(element);
        if (candidate.tag !== identity.tag) return false;
        if (identity.name) return candidate.name === identity.name &&
          (!['checkbox', 'radio'].includes(identity.type) || candidate.value === identity.value);
        if (identity.tag === 'SUMMARY') return true;
        return candidate.classes === identity.classes && candidate.text === identity.text;
      });
    }

    static renderFilters(parsedHTML) {
      // Shopify can omit/reorder groups. Match parameter names, never numeric IDs.
      const focused = document.activeElement;
      const focusForm = focused?.closest('form');
      const focusGroup = focused?.closest('details');
      const focus = focusForm ? {
        form: focusForm.id, group: FacetFiltersForm.filterGroupKey(focusGroup),
        identity: FacetFiltersForm.focusIdentity(focused)
      } : null;
      let activeDrawer = null;
      ['FacetFiltersForm', 'FacetFiltersFormMobile'].forEach(id => {
        const target = document.getElementById(id);
        const source = parsedHTML.getElementById(id);
        if (!target || !source) return;
        const groups = new Map(Array.from(target.querySelectorAll('details')).map(detail => [
          FacetFiltersForm.filterGroupKey(detail), {
            open: detail.open, menuOpening: detail.classList.contains('menu-opening'),
            accordionVisible: detail.closest('accordion-button')?.classList.contains('animated-dropdown--visible'),
            accordionActive: detail.closest('accordion-button')?.classList.contains('animated-dropdown--active'),
            buttonOpen: detail.querySelector('summary')?.classList.contains('animated-dropdown__button--open'),
            moreExpanded: detail.querySelector('.facets-list__button-checkbox')?.checked,
            scrollTop: detail.querySelector('.mobile-facets__submenu')?.scrollTop || 0
          }
        ]));
        const drawer = target.closest('facets-drawer');
        // NavigationDrawer's delayed callbacks refer to the old details nodes.
        drawer?.submenuTimers?.forEach(timer => clearTimeout(timer));
        drawer?.submenuTimers?.clear();
        target.innerHTML = source.innerHTML;
        target.querySelectorAll('details').forEach(detail => {
          const key = FacetFiltersForm.filterGroupKey(detail);
          const previous = key && groups.get(key);
          if (previous) {
            detail.open = previous.open;
            const more = detail.querySelector('.facets-list__button-checkbox');
            if (more && typeof previous.moreExpanded === 'boolean') more.checked = previous.moreExpanded;
            const accordion = detail.closest('accordion-button');
            if (accordion) {
              accordion.classList.toggle('animated-dropdown--visible', !!previous.accordionVisible);
              accordion.classList.toggle('animated-dropdown--active', !!previous.accordionActive);
              detail.querySelector('summary')?.classList.toggle('animated-dropdown__button--open', !!previous.buttonOpen);
            }
            // An open mobile group may still have its opening timer pending.
            detail.classList.toggle('menu-opening', previous.open &&
              (previous.menuOpening || detail.classList.contains('mobile-facets__details')));
            const panel = detail.querySelector('.mobile-facets__submenu');
            if (panel) panel.scrollTop = previous.scrollTop;
          }
          FacetFiltersForm.setSummarySemantics(detail);
        });
        if (drawer) {
          drawer.inner = target.querySelector('.mobile-facets__inner');
          drawer.checkMenuState?.();
          if (drawer.hasAttribute('open')) activeDrawer = drawer;
        }
      });
      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);
      let replacement = null;
      if (focus && ['FacetFiltersForm', 'FacetFiltersFormMobile'].includes(focus.form)) {
        const form = document.getElementById(focus.form);
        const group = focus.group && Array.from(form?.querySelectorAll('details') || [])
          .find(detail => FacetFiltersForm.filterGroupKey(detail) === focus.group);
        replacement = FacetFiltersForm.findFocusReplacement(focus.group ? group : form, focus.identity);
        // A response may legitimately remove the focused option or whole group.
        replacement ||= group?.querySelector('summary') || form?.querySelector('summary, button');
      }
      let drawerOwnsFocus = false;
      if (activeDrawer && typeof trapFocus === 'function') {
        // The existing trap retained removed elements. Rebind to the restored panel.
        let panel = activeDrawer.querySelector('details[open].menu-opening > .mobile-facets__submenu') ||
          activeDrawer.querySelector('[role="dialog"]');
        drawerOwnsFocus = !!(replacement && activeDrawer.contains(replacement));
        if (drawerOwnsFocus && panel && !panel.contains(replacement)) {
          panel = activeDrawer.querySelector('[role="dialog"]') || panel;
        }
        // trapFocus must bind focusin before the replacement receives first focus.
        if (panel) trapFocus(panel, drawerOwnsFocus ? replacement : false);
        else drawerOwnsFocus = false;
      }
      if (!drawerOwnsFocus) replacement?.focus({ preventScroll: true });
    }

    static renderActiveFacets(html) {
      const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-sidebar'];

      activeFacetElementSelectors.forEach((selector) => {
        const activeFacetsElement = html.querySelector(selector);

        if (!activeFacetsElement) return;
        const target = document.querySelector(selector);
        if (target) target.innerHTML = activeFacetsElement.innerHTML;
      })

      FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderAdditionalElements(html) {
      const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

      mobileElementSelectors.forEach((selector) => {
        if (!html.querySelector(selector)) return;
        const target = document.querySelector(selector);
        if (target) target.innerHTML = html.querySelector(selector).innerHTML;
      });
    }

    static renderCounts(source, target) {
      const targetElement = target.querySelector('.sidebar-facets__selected');
      const sourceElement = source.querySelector('.sidebar-facets__selected');

      if (sourceElement && targetElement) {
        target.querySelector('.sidebar-facets__selected').outerHTML = source.querySelector('.sidebar-facets__selected').outerHTML;
      }
    }

    static updateURLHash(searchParams) {
      history.pushState({
        ...history.state,
        searchParams
      }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }

    static getSections() {
      return [{
        section: document.getElementById('product-grid').dataset.id,
      }]
    }

    onSubmitHandler(event) {
      if (!this.isConnected || !event.target.isConnected) return;
      if (event.target.classList.contains('js-facets-ignore-checkbox')) {
        return;
      }

      event.preventDefault();
      document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

      const form = event.target.closest('form');
      const ignoreInputs = form.querySelectorAll('.js-facets-no-input');
      ignoreInputs.forEach((element) => element.setAttribute('disabled', true));
      const formData = new FormData(form);
      ignoreInputs.forEach((element) => element.removeAttribute('disabled'));
      const searchParams = new URLSearchParams(formData).toString();
      FacetFiltersForm.renderPage(searchParams, event);
    }

    onActiveFilterClick(event) {
      event.preventDefault();
      document.querySelectorAll('facet-filters-form').forEach(form => clearTimeout(form.submitTimer));
      FacetFiltersForm.toggleActiveFacets();
      const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
      FacetFiltersForm.renderPage(url);
    }
  }

  FacetFiltersForm.requestId = 0;
  FacetFiltersForm.filterData = [];
  FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
  FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
  customElements.define('facet-filters-form', FacetFiltersForm);
  FacetFiltersForm.setListeners();

  class PriceRange extends HTMLElement {
    connectedCallback() {
      const connection = this.connection = (this.connection || 0) + 1;
      themeBase.onDOMContentLoadedTrigger(() => {
        if (!this.isConnected || connection !== this.connection || this.listeners) return;
        this.listeners = new AbortController();
        this.priceSlider = this.closest('facet-filters-form')?.querySelector('price-slider');
        this.querySelectorAll('input').forEach(input => input.addEventListener('change', this.onRangeChange.bind(this), { signal: this.listeners.signal }));
        this.setMinAndMaxValues();
      });
    }

    disconnectedCallback() {
      this.connection += 1;
      this.listeners?.abort();
      this.listeners = null;
    }

    onRangeChange(event) {
      this.adjustToValidValues(event.currentTarget);
      this.setMinAndMaxValues();
    }

    setMinAndMaxValues() {
      const inputs = this.querySelectorAll('input');
      const minInput = inputs[0];
      const maxInput = inputs[1];
      if (maxInput.value) minInput.setAttribute('max', maxInput.value);
      if (minInput.value) maxInput.setAttribute('min', minInput.value);
      if (minInput.value === '') maxInput.setAttribute('min', 0);
      if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
    }

    adjustToValidValues(input) {
      const value = Number(input.value);
      const min = Number(input.getAttribute('min'));
      const max = Number(input.getAttribute('max'));
      let wasAdjusted = false;

      if (value < min) {
        input.value = min;
        wasAdjusted = true;
      }
      if (value > max) {
        input.value = max;
        wasAdjusted = true;
      }
      if (wasAdjusted && this.priceSlider) {
        this.priceSlider.onFormInput();
      }
    }
  }

  customElements.define('price-range', PriceRange);

  class PriceSlider extends HTMLElement {
    constructor() {
      super();
      this.onResizeBound = this.onResize.bind(this);
      this.onMouseupBound = this.onBodyMouseup.bind(this);
      this.onMousemoveBound = this.onBodyMousemove.bind(this);
      this.onFormInputBound = this.onFormInput.bind(this);
      this.onMousedownBound = event => this.onButtonMousedown(event.currentTarget, event);
    }

    connectedCallback() {
      const connection = this.connection = (this.connection || 0) + 1;
      themeBase.onDOMContentLoadedTrigger(() => {
        if (!this.isConnected || connection !== this.connection || this.sliderConnected) return;
        this.sliderConnected = true;
        this.enableSteps = false;
        this.form = this.closest('facet-filters-form');
        this.formAction = this.form.querySelector('form');
        this.priceRange = this.form.querySelector('price-range');
        this.inputs = this.priceRange.querySelectorAll('input');
        this.fromInput = this.inputs[0];
        this.toInput = this.inputs[1];
        this.wrapper = this.querySelector('.price-slider__wrapper');
        this.min = this.querySelector('.price-slider__min');
        this.max = this.querySelector('.price-slider__max');
        this.bar = this.querySelector('.price-slider__bar');
        this.barOutput = this.querySelector('.price-slider__bar-output');
        this.sideBarOutput = this.querySelector('.price-slider__side-bar-output');
        this.fromSideOutput = this.querySelector('.price-slider__side-from-output');
        this.toSideOutput = this.querySelector('.price-slider__side-to-output');
        this.fromButton = this.querySelector('.price-slider__button-from');
        this.fromButtonOutput = this.querySelector('.price-slider__button-from-output');
        this.toButton = this.querySelector('.price-slider__button-to');
        this.toButtonOutput = this.querySelector('.price-slider__button-to-output');
        this.hideCoins = this.priceRange.dataset.hideCoins === 'true';
        this.usesCommaDecimals = this.priceRange.dataset.usesCommaDecimals === 'true';
        this.step = Number(this.priceRange.dataset.step);

        this.saveStartValues();
        this.saveStartPercents();
        this.calculateStepValues();
        this.onResize();
        window.addEventListener('MutationObserver', this.onResizeBound);
        window.addEventListener('updatePriceSlider', this.onResizeBound);
        window.addEventListener('throttle30fpsResize', this.onResizeBound);
        this.classList.add('price-slider--initialized');
        [this.fromButton, this.toButton].forEach(element => element.addEventListener('mousedown', this.onMousedownBound));
        document.body.addEventListener('mouseup', this.onMouseupBound);
        document.body.addEventListener('mousemove', this.onMousemoveBound);
        this.formAction.addEventListener('input', this.onFormInputBound);
      });
    }

    disconnectedCallback() {
      this.connection += 1;
      ['MutationObserver', 'updatePriceSlider', 'throttle30fpsResize'].forEach(name => window.removeEventListener(name, this.onResizeBound));
      document.body.removeEventListener('mouseup', this.onMouseupBound);
      document.body.removeEventListener('mousemove', this.onMousemoveBound);
      this.formAction?.removeEventListener('input', this.onFormInputBound);
      [this.fromButton, this.toButton].forEach(button => button?.removeEventListener('mousedown', this.onMousedownBound));
      if (this.fromButton && this.toButton) this.changeStatus(false);
      this.sliderConnected = false;
      this.disableOnFormInput = false;
    }

    onResize() {
      if (!this.isConnected || !this.wrapper) return;
      this.calculateSizes();
      this.updateOutputsPositions();
    }

    onButtonMousedown(element, event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.wrapperWidth === 0) this.onResize();

      this.wasChanged = false;

      this.changeStatus(true, element);
      this.saveStartButtonEventData(event);
    }

    onBodyMouseup() {
      if (!this.isDragged) return;
      if (this.wasChanged) {
        this.disableOnFormInput = true;

        this.disableSlider();
        this.saveDraggedButtonPercent();
        this.triggerOnInputEvents();
      }

      this.changeStatus(false);
    }

    onBodyMousemove(event) {
      if (!this.isDragged) return;

      const resultDraggedPercent = this.calculateDraggedButtonPosition(event);

      if (this.lastUpdatedDraggedPercent === resultDraggedPercent) return;

      this.lastUpdatedDraggedPercent = resultDraggedPercent;
      this.wasChanged = true;

      this.updateDraggedButton();
      this.updateBarPosition();
      this.calculateValuesFromPercents();
      this.formatValuesToMoney();
      this.updateOutputs();
      this.updateOutputsPositions();
      this.formatMoneyToInputValues();
      this.updateInputs();
    }

    onFormInput() {
      if (this.disableOnFormInput) {
        this.disableOnFormInput = false;
        return;
      }

      this.saveStartValues();
      this.calculateStepValues();
      this.calculatePercentsFromValues();
      this.checkPercentLimits();
      this.updateButtons();
      this.updateBarPosition();
      this.formatValuesToMoney();
      this.updateOutputs();
      this.updateOutputsPositions();
    }

    disableSlider() {
      this.classList.add('price-slider--disable');
    }

    saveStartValues() {
      this.minValue = this.fromInput.getAttribute('min') * 100;
      this.maxValue = this.toInput.getAttribute('max') * 100;

      const fromValue = this.fromInput.value;
      const toValue = this.toInput.value;

      this.fromValue = fromValue ? fromValue * 100 : this.minValue;
      this.toValue = toValue ? toValue * 100 : this.maxValue;
      this.valuePerOnePercent = this.maxValue / 100;
    }

    saveStartPercents() {
      this.fromPercent = this.fromButton.style[getLogicalProperty('left')] ? Number(this.fromButton.style[getLogicalProperty('left')].replace('%', '')) : 0;
      this.toPercent = this.toButton.style[getLogicalProperty('left')] ? Number(this.toButton.style[getLogicalProperty('left')].replace('%', '')) : 100;
    }

    checkPercentLimits() {
      this.fromPercent = this.checkFromToPercent(this.checkMinMaxPercent(this.fromPercent), 'from');
      this.toPercent = this.checkFromToPercent(this.checkMinMaxPercent(this.toPercent), 'to');
    }

    calculateStepValues() {
      this.percentSteps = [];

      const difference = this.maxValue - this.minValue;
      const stepsAmount = Math.ceil(difference / this.step);

      for (let i = 0; i < stepsAmount; i++) {
        const stepValue = this.step * i;
        const stepPercent = stepValue / (difference / 100);

        this.percentSteps.push(stepPercent);
      }

      if (this.percentSteps[stepsAmount - 1] !== 100) this.percentSteps.push(100);
    }

    getNamespace() {
      return this.draggedButton === this.fromButton ? 'from' : 'to';
    }

    calculateSizes() {
      this.wrapperWidth = this.wrapper.clientWidth;
    }

    changeStatus(isDragged, element) {
      if (isDragged) {
        this.classList.add('price-slider--is-dragged');
        element.classList.add('price-slider__button--is-dragged');
        this.draggedButton = element;
        this.namespace = this.getNamespace();
      } else {
        this.classList.remove('price-slider--is-dragged');
        [this.fromButton, this.toButton].forEach(element => element.classList.remove('price-slider__button--is-dragged'));
        this.draggedButton = null;
        this.namespace = null;
        this.draggedPercent = null;
      }

      this.isDragged = isDragged;
    }

    saveStartButtonEventData(event) {
      this.startClientX = getLogicalClientX(event);
    }

    saveDraggedButtonPercent() {
      this[`${this.getNamespace()}Percent`] = this.draggedPercent;
    }

    getNamespacePercent(namespace) {
      return this.namespace === namespace ? this.draggedPercent : this[`${namespace}Percent`];
    }

    checkPercentSteps(percent) {
      let resultPercentStepIndex;

      this.percentSteps.forEach((stepPercent, index) => {
        if (percent === stepPercent) {
          resultPercentStepIndex = index;
          return false;
        } else if (percent > stepPercent) {
          if (this.percentSteps[index + 1]) {
            if (percent < this.percentSteps[index + 1]) {
              resultPercentStepIndex = index;
              return false;
            }
          } else {
            resultPercentStepIndex = stepPercent;
            return false;
          }
        }
      });

      return this.percentSteps[resultPercentStepIndex];
    }

    checkMinMaxPercent(percent) {
      return Math.min(Math.max(percent, 0), 100);
    }

    checkFromToPercent(percent, namespace = this.namespace) {
      return namespace === 'from' ? Math.min(percent, this.toPercent) : Math.max(percent, this.fromPercent);
    }

    calculateDraggedButtonPosition(event) {
      const difference = getLogicalClientX(event) - this.startClientX;
      const differencePercent = difference / (this.wrapperWidth / 100);
      const draggedStartPercent = this[`${this.namespace}Percent`];
      const resultDraggedPercent = this.checkFromToPercent(this.checkPercentSteps(this.checkMinMaxPercent(draggedStartPercent + differencePercent)));
      this.draggedPercent = resultDraggedPercent;
      return resultDraggedPercent;
    }

    updateDraggedButton() {
      this.draggedButton.style[getLogicalProperty('left')] = `${this.draggedPercent}%`;
    }

    updateButtons() {
      this.fromButton.style[getLogicalProperty('left')] = `${this.fromPercent}%`;
      this.toButton.style[getLogicalProperty('left')] = `${this.toPercent}%`;
    }

    updateBarPosition() {
      const startPercent = this.getNamespacePercent('from');
      const widthPercent = this.getNamespacePercent('to') - startPercent;

      this.bar.style[getLogicalProperty('left')] = `${startPercent}%`;
      this.bar.style.width = `${widthPercent}%`;
    }

    calculateValuesFromPercents() {
      this.fromValue = this.valuePerOnePercent * this.getNamespacePercent('from');
      this.toValue = this.valuePerOnePercent * this.getNamespacePercent('to');
    }

    calculatePercentsFromValues() {
      this.fromPercent = this.fromValue / this.valuePerOnePercent;
      this.toPercent = this.toValue / this.valuePerOnePercent;
    }

    formatValuesToMoney() {
      const moneyFormat = !this.hideCoins ? Shopify.money_format : Shopify.money_format.replace(/\{\{\s*(\w+)\s*\}\}/, '{{amount_no_decimals}}');

      this.fromFormatedMoney = Shopify.formatMoney(this.fromValue, moneyFormat);
      this.toFormatedMoney = Shopify.formatMoney(this.toValue, moneyFormat);
    }

    formatMoneyToInputValues() {
      this.fromInputValue = (this.fromValue / 100).toFixed(2);
      this.toInputValue = (this.toValue / 100).toFixed(2);

      if (Shopify.money_format.indexOf('amount_with_comma_separator') !== -1) {
        this.usesCommaDecimals = false;
      }

      if (this.usesCommaDecimals) {
        this.fromInputValue = this.fromInputValue.replace('.', '').replace(',', '.');
        this.toInputValue = this.toInputValue.replace('.', '').replace(',', '.');
      } else {
        this.fromInputValue = this.fromInputValue.replace(',', '');
        this.toInputValue = this.toInputValue.replace(',', '');
      }
      if (Shopify.money_format.indexOf('amount_no_decimals') !== -1) {
        this.fromInputValue = Number(this.fromInputValue).toFixed(0);
        this.toInputValue = Number(this.toInputValue).toFixed(0);
      }
    }

    updateOutputs() {
      const fromFormatedMoney = this.fromFormatedMoney;
      const toFormatedMoney = this.toFormatedMoney;
      const barOutput = fromFormatedMoney === toFormatedMoney ? fromFormatedMoney : `${fromFormatedMoney} — ${toFormatedMoney}`;

      this.fromSideOutput.innerHTML = fromFormatedMoney;
      this.toSideOutput.innerHTML = toFormatedMoney;
      this.fromButtonOutput.innerHTML = fromFormatedMoney;
      this.toButtonOutput.innerHTML = toFormatedMoney;
      this.barOutput.innerHTML = barOutput;
      this.sideBarOutput.innerHTML = barOutput;
    }

    updateOutputsPositions() {
      if (!this.isConnected || !this.wrapper) return;
      if (this.wrapperWidth === 0) return;

      const wrapperBounds = themeBounds.get(this.wrapper);
      const barBounds = themeBounds.get(this.bar);
      const barOutputBounds = themeBounds.get(this.barOutput);
      const minBounds = themeBounds.get(this.min);
      const maxBounds = themeBounds.get(this.max);
      const fromSideOutputBounds = themeBounds.get(this.fromSideOutput);
      const toSideOutputBounds = themeBounds.get(this.toSideOutput);
      const fromButtonBounds = themeBounds.get(this.fromButton);
      const fromButtonOutputBounds = themeBounds.get(this.fromButtonOutput);
      const toButtonBounds = themeBounds.get(this.toButton);
      const toButtonOutputBounds = themeBounds.get(this.toButtonOutput);
      let visibleBarOutput = false;

      if (fromSideOutputBounds.right >= toSideOutputBounds.left || fromButtonOutputBounds.right >= toButtonOutputBounds.left || fromSideOutputBounds.right >= toButtonOutputBounds.left || fromButtonOutputBounds.right >= toSideOutputBounds.left) {
        this.classList.add('price-slider--visible-bar-output');
        visibleBarOutput = true;
      } else {
        this.classList.remove('price-slider--visible-bar-output');
      }
      if (visibleBarOutput && barBounds.left + barBounds.width / 2 - barOutputBounds.width / 2 < wrapperBounds.left) {
        this.classList.add('price-slider--bar-output-fixed-left');
      } else if (visibleBarOutput && barBounds.left + barBounds.width / 2 + barOutputBounds.width / 2 > wrapperBounds.right) {
        this.classList.add('price-slider--bar-output-fixed-right');
      } else {
        this.classList.remove('price-slider--bar-output-fixed-left', 'price-slider--bar-output-fixed-right');
      }
      if (fromButtonOutputBounds.left > minBounds.right && (!visibleBarOutput || barOutputBounds.left > minBounds.right)) {
        this.classList.add('price-slider--visible-min');
      } else {
        this.classList.remove('price-slider--visible-min');
      }
      if (toButtonOutputBounds.right < maxBounds.left && (!visibleBarOutput || barOutputBounds.right < maxBounds.left)) {
        this.classList.add('price-slider--visible-max');
      } else {
        this.classList.remove('price-slider--visible-max');
      }
      if (fromButtonBounds.left > fromSideOutputBounds.left + fromSideOutputBounds.width / 2) {
        this.classList.add('price-slider--visible-button-from-output');
      } else {
        this.classList.remove('price-slider--visible-button-from-output');
      }
      if (toButtonBounds.right < toSideOutputBounds.right - toSideOutputBounds.width / 2) {
        this.classList.add('price-slider--visible-button-to-output');
      } else {
        this.classList.remove('price-slider--visible-button-to-output');
      }
    }

    updateInputs() {
      this.fromInput.value = this.fromValue !== this.minValue ? this.fromInputValue : '';
      this.toInput.value = this.toValue !== this.maxValue ? this.toInputValue : '';
    }

    triggerOnInputEvents() {
      this.formAction.dispatchEvent(new Event('input'));
    }
  }

  customElements.define('price-slider', PriceSlider);

  class FacetRemove extends HTMLElement {
    constructor() {
      super();
      themeBase.onDOMContentLoadedTrigger(() => {
        if (!this.isConnected) return;
        this.querySelector('a').addEventListener('click', (event) => {
          event.preventDefault();
          const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
          document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
          form.onActiveFilterClick(event);
        });
      });
    }
  }

  customElements.define('facet-remove', FacetRemove);

  class FacetSortingCloneForm extends HTMLElement {
    connectedCallback() {
      const connection = this.connection = (this.connection || 0) + 1;
      themeBase.onDOMContentLoadedTrigger(() => {
        if (!this.isConnected || connection !== this.connection || this.listeners) return;
        this.listeners = new AbortController();
        this.querySelector('form')?.addEventListener('input', event => {
          clearTimeout(this.submitTimer);
          FacetFiltersForm.cancelPending();
          this.submitTimer = setTimeout(() => {
            if (this.isConnected && event.target.isConnected) this.onSubmitHandler(event);
          }, 100);
        }, { signal: this.listeners.signal });
      });
    }

    disconnectedCallback() {
      this.connection += 1;
      clearTimeout(this.submitTimer);
      this.listeners?.abort();
      this.listeners = null;
    }

    onSubmitHandler(event) {
      event.preventDefault();

      const formData = new FormData(event.target.closest('form'));
      const form = document.querySelectorAll('facet-filters-form');
      const sortByCloneValue = formData.get('sort_by_clone');

      form.forEach(element => element.querySelector('[name="sort_by"]').value = sortByCloneValue);
      form[0].querySelector('form').dispatchEvent(new Event('input'));
    }
  }

  customElements.define('facet-sorting-clone-form', FacetSortingCloneForm);

  class FacetsPagination extends HTMLElement {
    constructor() {
      super();
      this.clickHandler = this.handleLoadMore.bind(this);
    }

    connectedCallback() {
      themeBase.onDOMContentLoadedTrigger(() => {
        this.initialize();
      });
    }

    initialize() {
      if (!this.isConnected) return;
      this.linkElements = this.querySelectorAll('a');
      this.linkElements.forEach(link => {
        link.addEventListener('click', this.clickHandler);
      });
    }

    handleLoadMore(evt) {
      evt.preventDefault();

      const gridContainer = document.getElementById('ProductGridContainer');
      gridContainer.querySelector('.collection').classList.add('loading');

      const clickedLink = evt.target.closest('a');
      const queryString = clickedLink.href.includes('?') ?
        clickedLink.href.split('?')[1] :
        '';

      const scrollConfig = this.dataset.scrollTo ? {
        callback: () => {
          document.getElementById(this.dataset.scrollTo)
            .scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
        }
      } : {};

      FacetFiltersForm.renderPage(queryString, null, true, scrollConfig);
    }

    disconnectedCallback() {
      if (this.linkElements?.length && this.clickHandler) {
        this.linkElements.forEach(link => {
          link.removeEventListener('click', this.clickHandler);
        });
      }
    }
  }

  customElements.define('facets-pagination', FacetsPagination);

  class FacetsLoadMore extends HTMLElement {
    constructor() {
      super();
      this.visibilityThreshold = 10;
      this.handleClick = this.performLoadMore.bind(this);
      this.handleScroll = this.checkElementVisibility.bind(this);
    }

    connectedCallback() {
      themeBase.onDOMContentLoadedTrigger(() => {
        this.setup();
      });
    }

    setup() {
      if (!this.isConnected) return;
      this.loadButton = this.querySelector('a');
      if (!this.loadButton) return;
      this.loadButton.addEventListener('click', this.handleClick);

      if (this.getAttribute('data-type') === 'infinite_scroll') {
        window.addEventListener('scroll', this.handleScroll);
        this.checkElementVisibility();
      }
    }

    checkElementVisibility() {
      const elementBounds = getLogicalRect(this);
      const windowHeight = window.innerHeight;

      if (elementBounds.top >= 0 &&
        elementBounds.bottom <= (windowHeight - this.visibilityThreshold)) {
        this.performLoadMore(new Event('scroll'));
      }
    }

    performLoadMore(evt) {
      evt.preventDefault();
      if (!this.isConnected || this.pendingLoad || FacetFiltersForm.pendingKey) return;
      this.pendingLoad = true;

      const queryString = this.loadButton.href.includes('?') ?
        this.loadButton.href.split('?')[1] :
        '';

      if (this.getAttribute('data-type') === 'infinite_scroll' && this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll);
        // Keep the stable callback for reconnect/retry.
      }

      this.loadButton.classList.add('loading');
      document.getElementById('ProductGridContainer')
        .querySelector('.collection')
        .classList.add('loading');

      FacetFiltersForm.renderPage(queryString, null, false, {
        renderProductMethod: 'append'
      }).finally(() => {
        this.pendingLoad = false;
        if (!this.isConnected) return;
        this.loadButton?.classList.remove('loading');
        // On failure keep the visible button available for an explicit retry.
      });
    }

    disconnectedCallback() {
      if (this.loadButton && this.handleClick) {
        this.loadButton.removeEventListener('click', this.handleClick);
      }
      if (this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll);
        // Keep the stable callback for reconnect/retry.
      }
    }
  }

  customElements.define('facets-load-more', FacetsLoadMore);

  class FacetsGrid extends HTMLElement {
    constructor() {
      super();
      themeBase.onDOMContentLoadedTrigger(() => this.init());
    }

    init() {
      if (!this.isConnected) return;
      this.addEventListener('input', (event) => {
        this.updateColumns(event.target.value);
        themeCookies.set(this.dataset.name, event.target.value);
        themeCookies.set(`default-${this.dataset.name}`, this.dataset.defaultValue);
      });

      if (!Shopify.designMode) {
        this.update();
      }

      this.checkedInput = this.querySelector('input:checked');
    }

    update() {
      const cookie = themeCookies.get(this.dataset.name);
      const defaultValue = themeCookies.get(`default-${this.dataset.name}`);

      if (cookie && defaultValue === this.dataset.defaultValue) {
        this.updateColumns(cookie);
        this.updateInputs(cookie);
      } else if (this.checkedInput) {
        this.updateColumns(this.checkedInput.value);
        this.updateInputs(this.checkedInput.value);
      }
    }

    updateInputs(value) {
      this.querySelectorAll('input').forEach(input => input.checked = input.value === value);
    }

    updateColumns(value) {
      const columns = document.querySelectorAll('#product-grid > li');

      columns.forEach((column) => {
        let classList = column.dataset.gridClass ?
          column.dataset.gridClass.split(' ') :
          [...column.classList];

        classList = classList.filter(cls => cls.replace(/\d+/g, 'VALUE') !== this.dataset.className);

        classList.push(this.dataset.className.replace('VALUE', value));

        if (column.dataset.gridClass !== undefined) {
          column.dataset.gridClass = classList.join(' ');
        } else {
          column.className = classList.join(' ');
        }
      });
    }

  }

  customElements.define('facets-grid', FacetsGrid);
}
